import type { Registratie } from '@/types/domain';
import { getRegistratieService } from './serviceFactory';

/**
 * Offline-wachtrij.
 *
 * Registraties die niet konden worden weggeschreven (geen netwerk, 401, 500)
 * blijven in localStorage staan en worden opnieuw aangeboden zodra de browser
 * weer online is. Dit is de basis waarop een volledige offline-modus
 * (service worker + achtergrond-sync) later kan worden gebouwd.
 */

const SLEUTEL = 'afkoppelcoach.wachtrij.v1';

export interface WachtrijItem {
  registratie: Registratie;
  pogingen: number;
  laatsteFout?: string;
  toegevoegdOp: string;
}

type Luisteraar = (items: WachtrijItem[]) => void;

const luisteraars = new Set<Luisteraar>();
let bezigMetLegen = false;

function lees(): WachtrijItem[] {
  try {
    const rauw = localStorage.getItem(SLEUTEL);
    return rauw ? (JSON.parse(rauw) as WachtrijItem[]) : [];
  } catch {
    return [];
  }
}

function schrijf(items: WachtrijItem[]): void {
  try {
    localStorage.setItem(SLEUTEL, JSON.stringify(items));
  } catch {
    console.warn('Wachtrij kon niet worden opgeslagen; opslag is vol.');
  }
  luisteraars.forEach((fn) => fn(items));
}

export const offlineQueue = {
  items: lees,

  aantal(): number {
    return lees().length;
  },

  /** Zet een registratie in de wachtrij (of ververst een bestaande poging). */
  toevoegen(registratie: Registratie, fout?: string): void {
    const items = lees();
    const bestaand = items.findIndex((item) => item.registratie.id === registratie.id);
    const nieuw: WachtrijItem = {
      registratie,
      pogingen: bestaand >= 0 ? items[bestaand].pogingen + 1 : 1,
      laatsteFout: fout,
      toegevoegdOp: bestaand >= 0 ? items[bestaand].toegevoegdOp : new Date().toISOString(),
    };
    if (bestaand >= 0) items[bestaand] = nieuw;
    else items.push(nieuw);
    schrijf(items);
  },

  verwijderen(id: string): void {
    schrijf(lees().filter((item) => item.registratie.id !== id));
  },

  legen(): void {
    schrijf([]);
  },

  abonneer(fn: Luisteraar): () => void {
    luisteraars.add(fn);
    fn(lees());
    return () => luisteraars.delete(fn);
  },

  /**
   * Probeert alle wachtende registraties opnieuw op te slaan.
   * Geeft de succesvol gesynchroniseerde registraties terug.
   */
  async verwerk(): Promise<Registratie[]> {
    if (bezigMetLegen || !navigator.onLine) return [];
    bezigMetLegen = true;

    const service = getRegistratieService();
    const gelukt: Registratie[] = [];
    const resterend: WachtrijItem[] = [];

    for (const item of lees()) {
      try {
        gelukt.push(await service.opslaan(item.registratie));
      } catch (fout) {
        resterend.push({
          ...item,
          pogingen: item.pogingen + 1,
          laatsteFout: fout instanceof Error ? fout.message : String(fout),
        });
      }
    }

    schrijf(resterend);
    bezigMetLegen = false;
    return gelukt;
  },
};

/** Start automatische verwerking zodra de browser weer online komt. */
export function startOfflineSync(bijSucces: (registraties: Registratie[]) => void): () => void {
  const verwerk = async () => {
    const gelukt = await offlineQueue.verwerk();
    if (gelukt.length > 0) bijSucces(gelukt);
  };

  window.addEventListener('online', verwerk);
  // Ook direct proberen bij het opstarten van de app.
  void verwerk();

  return () => window.removeEventListener('online', verwerk);
}
